import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { Menu, X, IceCream2, LogOut, User, Globe } from 'lucide-react';
import i18n from '@/i18n';

export default function TopBar() {
  const { t }          = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { to: '/dashboard',   label: t('nav.dashboard') },
    { to: '/recipes',     label: t('nav.recipes') },
    { to: '/ingredients', label: t('nav.ingredients') },
    { to: '/suppliers',   label: t('nav.suppliers') },
    { to: '/kitchen',     label: t('nav.kitchen') },
    ...(isAdmin ? [
      { to: '/users',    label: t('nav.users') },
      { to: '/settings', label: t('nav.settings') },
    ] : []),
  ];

  const toggleLang = () => i18n.changeLanguage(i18n.language === 'en' ? 'es' : 'en');

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4">
      {/* Mobile: hamburger + logo */}
      <div className="flex items-center gap-3 md:hidden">
        <button
          onClick={() => setMobileOpen(v => !v)}
          className="rounded p-1 text-muted-foreground hover:text-foreground"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
        <Link to="/dashboard" className="flex items-center gap-2 font-semibold">
          <IceCream2 size={20} className="text-primary" />
          MyNevada
        </Link>
      </div>

      {/* Desktop: breadcrumb / title slot — empty on purpose, pages set their own heading */}
      <div className="hidden md:block" />

      {/* Right actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={toggleLang}
          className="flex items-center gap-1 rounded px-2 py-1 text-sm text-muted-foreground hover:text-foreground"
          title="Toggle language"
        >
          <Globe size={16} />
          {i18n.language.toUpperCase()}
        </button>

        <span className="hidden items-center gap-1 text-sm text-muted-foreground sm:flex">
          <User size={14} />
          {user?.username}
        </span>

        <button
          onClick={() => logout()}
          className="flex items-center gap-1 rounded px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          title={t('nav.logout')}
        >
          <LogOut size={16} />
          <span className="hidden sm:inline">{t('nav.logout')}</span>
        </button>
      </div>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="absolute inset-x-0 top-16 z-40 border-b border-border bg-card shadow-md md:hidden">
          <nav className="flex flex-col p-4 gap-1">
            {navItems.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  [
                    'rounded-md px-3 py-2 text-sm font-medium',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-accent',
                  ].join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
